import React from "react";

import { faPlus, faTrashCan } from "@fortawesome/pro-regular-svg-icons";

import { Input } from "src/base-components/Input";
import { Button } from "src/design-system/Button";
import { IconButton } from "src/design-system/Icon";

export type HeaderEntry = {
  key: string;
  value: string;
};

export const DEFAULT_HEADER_ENTRIES: HeaderEntry[] = [
  { key: "Content-Type", value: "application/json" },
];

export const headerEntriesToRecord = (
  entries: HeaderEntry[],
): Record<string, string> =>
  Object.fromEntries(
    entries
      .filter((entry) => entry.key.trim())
      .map((entry) => [entry.key.trim(), entry.value] as const),
  );

type SampleHeadersEditorProps = {
  dataLoc: string;
  disabled?: boolean;
  entries: HeaderEntry[];
  onChange: (entries: HeaderEntry[]) => void;
};

export const SampleHeadersEditor: React.FC<SampleHeadersEditorProps> = ({
  dataLoc,
  disabled = false,
  entries,
  onChange,
}) => (
  <div className="flex flex-col items-start gap-1">
    {entries.map((entry, index) => (
      <div key={index} className="flex w-full items-center gap-1">
        <Input
          data-loc={`${dataLoc}-key-${index}`}
          disabled={disabled}
          placeholder="Key"
          value={entry.key}
          fullWidth
          onChange={(event) =>
            onChange(
              entries.map((currentEntry, currentIndex) =>
                currentIndex === index
                  ? { ...currentEntry, key: event.target.value }
                  : currentEntry,
              ),
            )
          }
        />
        <Input
          data-loc={`${dataLoc}-value-${index}`}
          disabled={disabled}
          placeholder="Value"
          value={entry.value}
          fullWidth
          onChange={(event) =>
            onChange(
              entries.map((currentEntry, currentIndex) =>
                currentIndex === index
                  ? { ...currentEntry, value: event.target.value }
                  : currentEntry,
              ),
            )
          }
        />
        <IconButton
          dataLoc={`${dataLoc}-remove-${index}`}
          disabled={disabled}
          icon={faTrashCan}
          onClick={() => {
            const nextEntries = entries.filter(
              (_, currentIndex) => currentIndex !== index,
            );
            onChange(
              nextEntries.length > 0 ? nextEntries : [{ key: "", value: "" }],
            );
          }}
        />
      </div>
    ))}
    <Button
      dataLoc={`${dataLoc}-add`}
      isDisabled={disabled}
      iconLeft={faPlus}
      size="sm"
      variant="secondary"
      onClick={() => onChange([...entries, { key: "", value: "" }])}
    >
      Add header
    </Button>
  </div>
);
